import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { nanoid } from 'nanoid';
import cc from 'classcat';
import { actions as routerActions } from 'modules/Router';
import Api from 'services/Api';
import Analytics from 'modules/Analytics';
import SlotModule from 'modules/Slot';
import Modals from 'modules/Modals';
import User from 'modules/User';
import Wallet from 'modules/Wallet';
import FailedKyc from 'components/FailedKyc';
import SlotFrame from './SlotFrame';
import './slotRoom.css';

const { push, replace } = routerActions;
const isProd = process.env.NODE_STAGE === 'production';
const MOBILE_BREAKPOINT = 1024;

// Constrained ratio is 9/14
const PORTRAIT_CONSTRAINED_MAX_HEIGHT = 1188;
const PORTRAIT_CONSTRAINED_MAX_WIDTH = 500;
const ORIENTATION = {
  PORTRAIT: 'PORTRAIT',
  LANDSCAPE: 'LANDSCAPE'
};
// 44px is the deposit bar height
const DEPOSIT_BAR = 44;

const getHeight = (roomHeight, deposit) => (deposit ? roomHeight - DEPOSIT_BAR : roomHeight);
const getWidth = (roomWidth, deposit) => (deposit ? roomWidth + DEPOSIT_BAR : roomWidth);

const ASPECT_RATIOS = {
  0.5625: {
    width: '55.68vh',
    height: () => '100%',
    'max-width': () => '100%',
    'max-height': (roomWidth, deposit) =>
      deposit ? `${(roomWidth + 44) * 1.7778}px` : `${roomWidth * 1.7778}px`
  },
  1.7778: {
    width: '80%',
    height: (deposit) => (deposit ? 'calc(45vw + 44px)' : '45vw'),
    'max-width': (roomHeight, deposit) =>
      deposit ? `${(roomHeight - 44) * 1.7778}px` : `${roomHeight * 1.7778}px`,
    'max-height': () => '90%'
  }
};
const MOBILE_ASPECT_RATIOS = {
  0.5625: {
    width: '100%',
    height: () => '100%',
    'max-width': () => '100%',
    'max-height': () => '100%'
  },
  1.7778: {
    width: '100%',
    height: () => '100%',
    'max-width': () => '100%',
    'max-height': () => '100%'
  }
};
const PORTRAIT_CONSTRAINED_RATIOS = {
  0.6429: {
    width: '100%',
    height: () => '80%',
    'max-width': () => `${PORTRAIT_CONSTRAINED_MAX_WIDTH}px`,
    'max-height': (roomWidth) => {
      if (roomWidth * 0.6429 >= PORTRAIT_CONSTRAINED_MAX_WIDTH) {
        return `${PORTRAIT_CONSTRAINED_MAX_HEIGHT}px`;
      }
      return `${roomWidth * 1.5556}px`;
    }
  }
};
const PORTRAIT_CONSTRAINED_RATIOS_MOBILE = {
  0.6429: {
    width: '100%',
    height: () => '100%',
    'max-width': (roomHeight) => {
      if (roomHeight >= PORTRAIT_CONSTRAINED_MAX_HEIGHT) {
        return `${PORTRAIT_CONSTRAINED_MAX_WIDTH}px`;
      }
      return '100%';
    },
    'max-height': (roomWidth) => {
      if (roomWidth >= PORTRAIT_CONSTRAINED_MAX_WIDTH) {
        return `${PORTRAIT_CONSTRAINED_MAX_HEIGHT}px`;
      }
      return '100%';
    }
  }
};

const getRatios = (roomWidth, forcePortraitConstrained) => {
  const isMobile = roomWidth < MOBILE_BREAKPOINT;
  if (!forcePortraitConstrained) return isMobile ? MOBILE_ASPECT_RATIOS : ASPECT_RATIOS;
  return isMobile ? PORTRAIT_CONSTRAINED_RATIOS_MOBILE : PORTRAIT_CONSTRAINED_RATIOS;
};

class _SlotRoom extends Component {
  constructor(props) {
    super(props);
    this.mounted = Promise.resolve().then();
    this.redirecting = false;
  }

  componentDidMount() {
    this.props.initialize();
    this.mounted = Promise.resolve(true);
  }

  componentDidUpdate() {
    const { notFound, notFoundMsg, redirect404 } = this.props;

    if (notFound && !this.redirecting) {
      this.redirecting = true;
      redirect404(notFoundMsg);
    }
  }

  componentWillUnmount() {
    this.mounted = Promise.resolve();
    this.redirecting = false;
  }

  getFrameSize = (elem, callback) => () => {
    const width = elem.offsetWidth;
    const height = elem.offsetHeight;
    callback({ width, height });
  };

  initSize = async (elem) => {
    await this.mounted;
    this.frameResized(elem);
  };

  frameResized = (elem) => {
    if (elem && this.lobbyWrapper) {
      const { paymentMethodRefs, orientation } = this.props;
      // Test this.lobbyWrapper is mounted
      const roomWidth = this.lobbyWrapper.offsetWidth;
      const roomHeight = this.lobbyWrapper.offsetHeight;
      const currentRatio = roomWidth / roomHeight;
      const ratios = getRatios(roomWidth, orientation === ORIENTATION.PORTRAIT);
      const closestCorrectRatio = Object.keys(ratios).reduce((prev, curr) =>
        Math.abs(curr - currentRatio) < Math.abs(prev - currentRatio) ? curr : prev
      );
      elem.style.setProperty('width', ratios[closestCorrectRatio].width);
      elem.style.setProperty('height', ratios[closestCorrectRatio].height(paymentMethodRefs));
      elem.style.setProperty(
        'max-height',
        ratios[closestCorrectRatio]['max-height'](getWidth(roomWidth, paymentMethodRefs))
      );
      elem.style.setProperty(
        'max-width',
        ratios[closestCorrectRatio]['max-width'](getHeight(roomHeight, paymentMethodRefs))
      );
      this.getFrameSize(elem, (size) => {
        this.props.frameResizedAction(size.width, size.height, this.props.provider);
      })();
    }
  };

  frameOrientationChanged = () => {
    this.props.frameOrientationChanged(this.props.provider);
  };

  render() {
    const {
      match: { params },
      backgroundImageUrl,
      altBackgroundImageUrl,
      logoImageUrl,
      name,
      paymentMethods,
      kycStatus,
      paymentMethodRefs
    } = this.props;
    const frameClass = cc(['room-frame', paymentMethodRefs && 'room-frame--deposit']);
    const kycPass = kycStatus === User.constants.kyc.PASS;

    return (
      <div
        className="layout-wrap slots"
        style={{
          backgroundImage: `url('${
            kycPass ? backgroundImageUrl : altBackgroundImageUrl || backgroundImageUrl
          }')`
        }}
        ref={(elem) => {
          this.lobbyWrapper = elem;
        }}
      >
        {kycPass ? (
          <SlotFrame
            refName={params.refName}
            className={frameClass}
            frameResized={this.frameResized}
            frameOrientationChanged={this.frameOrientationChanged}
            initSize={this.initSize}
            placeholder={logoImageUrl}
            paymentMethods={paymentMethods}
            paymentMethodRefs={paymentMethodRefs}
          />
        ) : (
          <FailedKyc className="slots__failedKyc" />
        )}
        <Helmet>
          <title>{name} | MrQ</title>
        </Helmet>
      </div>
    );
  }
}

_SlotRoom.propTypes = {
  match: PropTypes.object.isRequired,
  orientation: PropTypes.string,
  backgroundImageUrl: PropTypes.string,
  altBackgroundImageUrl: PropTypes.string,
  logoImageUrl: PropTypes.string,
  name: PropTypes.string,
  provider: PropTypes.string,
  paymentMethods: PropTypes.object,
  kycStatus: PropTypes.oneOf(Object.values(User.constants.kyc)).isRequired,
  paymentMethodRefs: PropTypes.arrayOf(PropTypes.string),
  initialize: PropTypes.func.isRequired,
  frameResizedAction: PropTypes.func,
  notFoundMsg: PropTypes.string.isRequired,
  notFound: PropTypes.bool.isRequired
};

_SlotRoom.defaultProps = {
  backgroundImageUrl: '',
  orientation: 'landscape',
  altBackgroundImageUrl: '',
  logoImageUrl: '',
  name: '',
  provider: '',
  paymentMethods: null,
  paymentMethodRefs: null,
  frameResizedAction: null
};

const mapStateToProps = (state, props) => {
  const refName = props.match.params.refName;
  const slot = SlotModule.selectors.getRoom(state, refName);
  const notFound = SlotModule.selectors.getNotFound(state, refName);
  const notFoundMsg = SlotModule.selectors.getNotFoundMsg(state, refName);

  return {
    paymentMethodRefs: Wallet.selectors.getPaymentMethodRefs(state),
    paymentMethods: Wallet.selectors.getPaymentMethods(state),
    backgroundImageUrl: slot?.backgroundImageUrl,
    altBackgroundImageUrl: slot?.altBackgroundImageUrl,
    logoImageUrl: slot?.logoImageUrl,
    provider: slot?.provider,
    kycStatus: User.selectors.getKycStatus(state),
    name: slot?.name,
    orientation: slot?.orientation,
    notFoundMsg,
    notFound
  };
};

const mapDispatchToProps = (dispatch, { match }) => ({
  initialize: () => {
    Api.actions.wallet.listPaymentMethods()(dispatch);
    Api.actions.slot.assets({ refName: match.params.refName })(dispatch);
  },
  gotoLobby: () => dispatch(push('/secure/lobby')),
  frameResizedAction: (width, height, provider) =>
    dispatch(SlotModule.actions.frameResized(width, height, provider)),
  frameOrientationChanged: (provider) => {
    dispatch(SlotModule.actions.frameOrientationChanged(provider));
    dispatch(Analytics.actions.trackEvent('frameOrientationChanged'));
  },
  redirect404: (msg) => {
    dispatch(replace(`/secure/lobby`));
    dispatch(
      Modals.actions.create(nanoid(), {
        notification: true,
        creationDate: Date.now(),
        message: msg || 'Game does not exist. Please choose one of our wonderful games below!',
        expiration: 60000,
        announcement: true,
        pinned: true,
        blocking: false,
        closeOnNavigation: true,
        severity: 1,
        destroyOnClose: true
      })
    );
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(_SlotRoom);
